import { AfterViewInit, Component, EventEmitter, inject, Input, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { NgbActiveModal, NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { NgForm } from "@angular/forms";
import { merge, Observable, OperatorFunction, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import { EnumsService } from '../../../services/enums.service';
import { Asset } from '../../../models/asset.model';
import { assetTypeRequiresInstitution, INSTITUTION_BASED_ASSET_TYPES } from '../../../models/enums/asset-type-enum';
import { EnumerationItem } from 'projects/shared/models/enumeration-item.model';
import { MultiCheckboxPickerComponent } from 'projects/shared/multi-checkbox-picker/multi-checkbox-picker.component';
import { Address } from '../../../models/address-model';
import { AssetPendingNetSaleProceedsComponent } from '../asset-pending-net-sale-proceeds/asset-pending-net-sale-proceeds.component';
import { Borrower } from '../../../models/borrower-model';
import { ConfigurableFieldHostComponent } from '../../configurable-field-host.component';
import { WizardFlowServiceBase } from '../../../services/wizard/wizard-flow-service.base';
import { applyCashGiftDownPaymentSource } from './cash-gift-down-payment';

interface AccountRow {
  rowId: string;
  assetId: number;
  assetType: string | null;
  cashMarketValue: number | null;
  accountNumber: string | null;
  selectedBorrowers: EnumerationItem[];
}

/**
 * Everything the user changed inside the dialog, emitted in one shot on Ok.
 * The parent applies it as a single transaction against `mortgage.assets`:
 *   - `upsert` entries with an assetId that already exists in the list → edit
 *   - `upsert` entries with a new (negative) assetId → create
 *   - `removeAssetIds` → soft- or hard-delete from the list
 * On Cancel, nothing emits, so the dialog's internal edits/adds/deletes
 * never touch `mortgage.assets`.
 */
export interface BulkSaveTransaction {
  upsert: Asset[];
  removeAssetIds: number[];
}

@Component({
    selector: 'asset-dialog',
    templateUrl: 'asset-dialog.component.html',
    styleUrls: ['asset-dialog.component.scss'],
    standalone: false
})

export class AssetDialogComponent extends ConfigurableFieldHostComponent implements OnInit, AfterViewInit {

  private readonly _transloco = inject(TranslocoService);

  @ViewChild('assetInfoForm') assetInfoForm: NgForm | undefined;

  @ViewChild('institutionTypeahead', { static: false })
  institutionTypeahead: NgbTypeahead | undefined;

  @ViewChild(MultiCheckboxPickerComponent)
  borrowerPicker!: MultiCheckboxPickerComponent;

  // In multi-account mode there's one picker per row, so we need the full list.
  @ViewChildren(MultiCheckboxPickerComponent)
  borrowerPickers!: QueryList<MultiCheckboxPickerComponent>;

  @Input()
  existingInstitutions: string[] = [];

  @Input()
  lockedInstitution: string | undefined;

  /**
   * Opens the dialog directly in bulk-account mode without pre-locking the
   * institution. Used by the assets step's "Add Account" CTA so the user can
   * enter several accounts at a fresh bank without first creating one
   * single-asset round-trip. The institution typeahead is shown at the top
   * of the bulk card; the table appears once the user commits a value.
   */
  @Input()
  startInBulkMode: boolean = false;

  /**
   * Existing assets at the locked institution. Pre-populates the row table so
   * the user sees every account at this bank when they open the dialog —
   * whether they came in to add another or to edit an existing one. The
   * original assetIds are preserved so the parent's batched-save router can
   * tell which rows are edits and which are new creates.
   */
  @Input()
  seedAssets: Asset[] = [];

  public institutionFocus$ = new Subject<string>();
  public institutionClick$ = new Subject<string>();

  @ViewChild(AssetPendingNetSaleProceedsComponent)
  assetPendingNetSaleProceeds!: AssetPendingNetSaleProceedsComponent;

  private _asset: Asset;
  private _inEditMode: boolean = false;

  // True once the user picks/blurs the institution typeahead in the no-locked
  // bulk-add path. Locked or edit modes consider the institution already
  // committed (set in ngOnInit). Gates the rendering of the row table.
  private _institutionCommitted: boolean = false;

  public address: Address = new Address();

  possibleBorrowers: EnumerationItem[] = [];
  disabledBorrowers: EnumerationItem[] = [];
  selectedBorrowers: EnumerationItem[] = [];

  inCoApplyFlow: boolean = false;

  // Rows shown when the user is bulk-adding multiple accounts at one institution.
  accountRows: AccountRow[] = [];

  // assetIds of the rows the dialog opened with (from seedAssets). On save we
  // compare these against the current accountRows — any id that's no longer
  // present is a row the user removed via the in-dialog trash, so it gets
  // emitted as a deletion to the parent.
  private _seededAssetIds: number[] = [];

  // Per-instance sequence used by nextLocalId() to disambiguate ids generated
  // within the same millisecond. Without this, rapid back-to-back calls (e.g.
  // seeding multiple rows in a single ngOnInit) would yield identical ids and
  // collapse the @for trackBy, making every row display the same data.
  private _idCounter = 0;

  public integerPattern = {
    'X': { pattern: new RegExp('[1-9]') },
    'Y': { pattern: new RegExp('[0-9]') }
  }

  constructor(public activeModal: NgbActiveModal,
    private readonly _enumsService: EnumsService,
    private readonly _wizardFlowService: WizardFlowServiceBase) {
      super();
      this.inCoApplyFlow = this._wizardFlowService.inCoApplyFlow;
      this._asset = new Asset();
      this._asset.assetId = this.nextLocalId();
      const allBorrowers = this._wizardFlowService.context.mortgage.borrowers;
      this.possibleBorrowers = allBorrowers.map(b => new EnumerationItem(b.firstName + " " + b.lastName, b.borrowerId !== undefined ? b.borrowerId.toString() : (-1 * (new Date()).valueOf()).toString()));
  }

  @Output()
  saveClickedForCreate = new EventEmitter<Asset>();

  @Output()
  saveClickedForEdit = new EventEmitter<Asset>();

  /**
   * Fires once per Ok click in multi-account mode with the consolidated
   * result of everything the user did in the dialog — every row in its
   * final state plus the assetIds of seeded rows that were removed via
   * the in-dialog trash. The parent applies all of it in one pass and
   * refreshes the list once at the end. Each `upsert` carries its
   * `assetId`: positive id from a seed → edit; locally generated negative
   * id → create.
   */
  @Output()
  bulkSaved = new EventEmitter<BulkSaveTransaction>();

  @Input()
  public set asset(asset: Asset) {
    this._asset = asset;
    this.address.address1 = asset.holderAddress1;
    this.address.city = asset.holderCity;
    this.address.state = asset.holderState;
    this.address.zipCode = asset.holderZipCode;
    this._inEditMode = true;
  }

  public get assetTypes(): any[] {
    let assetTypes = this._enumsService.assetTypes;
    // Remove the "Automobile" type from the list of asset types
    assetTypes = assetTypes.filter((type) => type.value !== 'Automobile');
    if (this.isMultiAccountMode) {
      assetTypes = assetTypes.filter((type) => INSTITUTION_BASED_ASSET_TYPES.includes(type.value));
    }
    return assetTypes;
  }

  public get giftOrGrantSources(): any[] {
    return this._enumsService.giftOrGrantSources;
  }

  public get asset(): Asset {
    return this._asset;
  }

  public get inEditMode(): boolean {
    return this._inEditMode;
  }

  public get assetRequiresInstitution(): boolean {
    return assetTypeRequiresInstitution(this._asset?.assetType);
  }

  /**
   * The dialog is using the bulk-account UI shape when any of:
   *  - the user opened the dialog from the "Add another at this institution"
   *    button on a group card (lockedInstitution is set)
   *  - the user opened from the new top-level "Add Account" CTA (startInBulkMode)
   *  - the user is editing an existing institution-based asset (so they can
   *    correct it AND add more accounts at the same bank in one trip)
   */
  public get isMultiAccountMode(): boolean {
    if (this._inEditMode) return this.assetRequiresInstitution;
    return !!this.lockedInstitution || this.startInBulkMode;
  }

  /**
   * The row table is only rendered once the dialog has a committed institution.
   * For locked-institution and edit entry paths that's true from ngOnInit; for
   * the "Add Account" path the user has to first pick/blur an institution.
   */
  public get hasCommittedInstitution(): boolean {
    if (!this.isMultiAccountMode) return false;
    if (this.lockedInstitution || this._inEditMode) return true;
    return this._institutionCommitted && (this._asset?.depository ?? '').trim().length > 0;
  }

  public get header(): string {
    if (this._inEditMode) {
      return this._transloco.translate('wizard.assets.editingAsset');
    }
    return this._transloco.translate('wizard.assets.addAssetHeader');
  }

  onAssetTypeSelected= (assetType: string) => {
    if (assetType != '0') {
      this.asset.makeModel = undefined;
      this.asset.year = undefined;
    }
  }

  /**
   * Marks the typed institution as finalized. Called after the depository
   * value has settled — from `(change)` on the input (typed value committed)
   * or from `onInstitutionSelected` (typeahead pick).
   */
  commitInstitution = (): void => {
    if (!this.isMultiAccountMode) return;
    if (!((this._asset?.depository ?? '').trim())) return;
    this._institutionCommitted = true;
    if (this.accountRows.length === 0) {
      this.addAccountRow();
    }
  }

  /**
   * Handles a typeahead pick. NgbTypeahead fires `(selectItem)` *before* the
   * picked value lands in the bound model — committing straight off the event
   * is the only way to get the table to appear on selection without waiting
   * for a follow-up blur/change cycle.
   */
  onInstitutionSelected = (event: { item: string }): void => {
    const picked = (event?.item ?? '').toString();
    if (!picked.trim()) return;
    this._asset.depository = picked;
    this.commitInstitution();
  }

  onDepositoryChanged = (value: string) => {
    this._asset.depository = value;
    if (!(value ?? '').trim()) {
      // Cleared the institution — collapse the table (keep rows so the user's
      // entered data survives a re-commit) and wait for them to pick again.
      this._institutionCommitted = false;
    }
  }

  addAccountRow = (): void => {
    const previous = this.accountRows[this.accountRows.length - 1];
    // Carry forward only the previous row's owners — multiple accounts at one
    // bank typically share owners, so pre-filling those saves re-ticking. Asset
    // type stays empty: assuming "same type as the previous account" is
    // wrong more often than right, and a forced-pick is the safer default.
    const seededBorrowers = this.inCoApplyFlow
      ? []
      : (previous ? [...previous.selectedBorrowers] : [...this.selectedBorrowers]);
    this.accountRows.push({
      rowId: this.nextLocalId().toString(),
      assetId: this.nextLocalId(),
      assetType: null,
      cashMarketValue: null,
      accountNumber: null,
      selectedBorrowers: seededBorrowers,
    });
  }

  removeAccountRow = (rowId: string): void => {
    if (this.accountRows.length <= 1) return;
    this.accountRows = this.accountRows.filter(r => r.rowId !== rowId);
  }

  searchInstitutions: OperatorFunction<string, readonly string[]> = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(150), distinctUntilChanged());
    const clicksWithClosedPopup$ = this.institutionClick$.pipe(
      filter(() => !this.institutionTypeahead?.isPopupOpen())
    );
    const inputFocus$ = this.institutionFocus$;
    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
      map(term => {
        const list = this.existingInstitutions ?? [];
        if (!term) {
          return list.slice(0, 10);
        }
        const lowered = term.toLowerCase();
        return list.filter(item => item.toLowerCase().includes(lowered)).slice(0, 10);
      })
    );
  }

  onSaveClicked = () => {
    this.assetInfoForm?.form.markAllAsTouched();

    if (this.isMultiAccountMode) {
      this.saveMultipleAccounts();
      return;
    }

    if ((this.assetPendingNetSaleProceeds && this.assetPendingNetSaleProceeds.isValid
      && this.assetInfoForm?.form.valid)
        || (!this.assetPendingNetSaleProceeds && this.assetInfoForm?.form.valid)) {
      // LLS-641: commit the Cash Gift down-payment answer against the live mortgage
      // before the modal closes, so the Loan Type step sees Gift on the same save.
      applyCashGiftDownPaymentSource(this._wizardFlowService.context.mortgage, this.asset);
      this.activeModal.close('Save click');
      this.asset.holderAddress1 = this.address.address1;
      this.asset.holderCity = this.address.city;
      this.asset.holderZipCode = this.address.zipCode;
      this.asset.holderState = this.address.state;
      if (!assetTypeRequiresInstitution(this.asset.assetType)) {
        this.asset.depository = undefined;
        this.asset.accountNumber = undefined;
      }
      if (this.inCoApplyFlow) {
        this.asset.owningBorrowerIds = [<number>this._wizardFlowService.context.currentBorrower.borrowerId];
      } else {
        const owningBorrowerIds = this.borrowerPicker?.selectedItems.map(i => Number(i.value));
        this.asset.owningBorrowerIds = owningBorrowerIds;
      }

      if (this._inEditMode) {
        this.saveClickedForEdit.emit(this.asset);
      } else {
        this.saveClickedForCreate.emit(this.asset);
      }
    }
  }

  private saveMultipleAccounts(): void {
    if (!this.assetInfoForm?.form.valid) return;

    // Read live borrower selections from the per-row pickers. The pickers are
    // rendered in row order, so toArray() index-matches accountRows.
    const pickers = this.borrowerPickers?.toArray() ?? [];

    const institution = this.lockedInstitution ?? this._asset.depository;

    const assets: Asset[] = this.accountRows.map((row, idx) => {
      const a = new Asset();
      a.assetId = row.assetId;
      a.assetType = row.assetType as any;
      a.depository = institution;
      a.accountNumber = row.accountNumber ?? undefined;
      a.cashMarketValue = row.cashMarketValue ?? undefined;
      if (this.inCoApplyFlow) {
        a.owningBorrowerIds = [<number>this._wizardFlowService.context.currentBorrower.borrowerId];
      } else {
        const picker = pickers[idx];
        a.owningBorrowerIds = (picker?.selectedItems ?? row.selectedBorrowers ?? [])
          .map(s => Number(s.value));
      }
      return a;
    });

    // Any seeded assetId that's no longer in accountRows is a row the user
    // removed via the in-dialog trash. Combined with `upsert` (the current
    // rows in their final state), the parent has everything it needs to
    // reconcile the dialog's local list with `mortgage.assets` in one pass.
    const currentIds = new Set(this.accountRows.map(r => r.assetId));
    const removeAssetIds = this._seededAssetIds.filter(id => !currentIds.has(id));

    this.activeModal.close('Save click');
    this.bulkSaved.emit({ upsert: assets, removeAssetIds });
  }

  public get saveClickedEmitterForEdit(): EventEmitter<Asset> {
    return this.saveClickedForEdit;
  }

  public get saveClickedEmitterForCreate(): EventEmitter<Asset> {
    return this.saveClickedForCreate;
  }

  public get bulkSavedEmitter(): EventEmitter<BulkSaveTransaction> {
    return this.bulkSaved;
  }

  ngOnInit() {
    this.fieldsToConfig = this.fieldsToConfig ?? {};

    if (this._inEditMode && this.assetRequiresInstitution) {
      // Edit flow on an institution-based asset: show every account at this
      // bank so the user can correct the one they clicked AND see/edit any
      // siblings in the same trip. Falls back to just the clicked asset if
      // the parent didn't pass the wider list.
      this.seedAccountRowsFromAssets(this.seedAssets.length > 0 ? this.seedAssets : [this._asset]);
    } else if (this.lockedInstitution && !this._inEditMode) {
      this._asset.depository = this.lockedInstitution;
      // "Add another at this institution": pre-populate with the existing
      // accounts at this bank, then add an empty row at the end where the
      // new account gets entered. Existing rows are editable too — saves a
      // round trip if the user spots a typo on a sibling while adding.
      if (this.seedAssets.length > 0) {
        this.seedAccountRowsFromAssets(this.seedAssets);
      }
      this.addAccountRow();
    }
    // startInBulkMode without lockedInstitution: no row seeded yet — the user
    // first picks an institution; commitInstitution() seeds the first row.
  }

  /**
   * Builds the row table from a set of existing Asset objects. Each row's
   * `assetId` carries through from the original asset so the parent's save
   * router can match it back to `mortgage.assets` and route the emit as an
   * edit rather than a create.
   */
  private seedAccountRowsFromAssets(assets: Asset[]): void {
    this.accountRows = assets.map(asset => {
      const ownerIds = (asset.owningBorrowerIds ?? []).map(id => id.toString());
      const ownerItems = this.possibleBorrowers.filter(b => ownerIds.includes(b.value));
      return {
        rowId: this.nextLocalId().toString(),
        assetId: asset.assetId!,
        assetType: (asset.assetType as string) ?? null,
        cashMarketValue: asset.cashMarketValue ?? null,
        accountNumber: asset.accountNumber ?? null,
        selectedBorrowers: ownerItems,
      };
    });
    // Snapshot the seeded ids so saveMultipleAccounts can tell which existing
    // assets the user removed via the in-dialog trash.
    this._seededAssetIds = assets.map(a => a.assetId!).filter(id => id != null);
  }

  /**
   * Per-instance unique ID generator for local (never-persisted) assets and
   * row tracking. Combines `Date.now()` with a sequence counter so successive
   * calls within the same millisecond don't collide — important when seeding
   * the row table from multiple existing assets at once.
   *
   * Bit layout (31 bits total, then negated to mark as "local/unsaved"):
   *   [23-bit timestamp] [8-bit counter] → max magnitude 0x7fffffff
   *
   * The magnitude stays strictly within Int32 range — the backend's `assetId`
   * column is Int32 and rejects values outside ±2,147,483,647.
   */
  private nextLocalId(): number {
    this._idCounter = (this._idCounter + 1) & 0xff;
    const ts = Date.now() & 0x7fffff;
    return -((ts * 0x100) + this._idCounter);
  }

  private setBorrowerPickerValues() {
    if (!this.inEditMode) {
      return;
    }
    if (this.isMultiAccountMode) {
      // Bulk-edit pre-seeds the row's borrower selection in ngOnInit — the
      // single-asset borrowerPicker isn't rendered in this layout.
      return;
    }
    const owningBorrowerIds: number[] = this.asset.owningBorrowerIds;
    if (!owningBorrowerIds) {
      return;
    }
    const enumerationItems: EnumerationItem[] = [];
    for (let i=0; i < owningBorrowerIds.length; i++){
      const borrower: Borrower | undefined = this._wizardFlowService.context.mortgage.borrowers.find(b => b.borrowerId === owningBorrowerIds[i]);
      if (borrower) {
        const enumerationItem = new EnumerationItem(borrower.firstName + " " +  borrower.lastName, borrower.borrowerId!.toString());
        enumerationItems.push(enumerationItem);
      }
    }
    this.borrowerPicker.selectedItems = enumerationItems;
  }

  ngAfterViewInit() {
      this.setBorrowerPickerValues();
  }
}
